import { useEffect, useState } from "react";
import { useLocation, useParams, Link as RouterLink } from "react-router-dom";
import { useAuthentication, useDeal, useDialog } from "hooks";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
 BasePage,
 Carousel,
 PriceTypography,
 RatingTypography,
 ShareButton,
 TimeTypography,
 NotificationTypography,
 LikeChip,
 ChipButton,
 ReviewList,
 ChatButton,
} from "components";
import DangerousIcon from "@mui/icons-material/Dangerous";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import NoFoodIcon from "@mui/icons-material/NoFood";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import {
 Box,
 Grid,
 Card,
 CardHeader,
 CardContent,
 Typography,
 Chip,
 Tab,
 Link,
 Alert,
} from "@mui/material";
import { originalDays, getStatusWarningMessage } from "utilities";
import { DealPageSkeleton } from "./skeleton";
import { RelatedDealGrid } from "components";
import { API_ENDPOINTS } from "constants";

export function DealPage() {
 const location = useLocation();
 const { confirm } = useDialog();
 const { dealId } = useParams();
 const { user } = useAuthentication();
 const [tab, setTab] = useState("deals");
 const { deal, loading, error, likeDeal, unlikeDeal } = useDeal(dealId);

 useEffect(() => {
  window.scrollTo(0, 0);
 }, [location.pathname]);

 const handleTabChange = (_, newTab) => {
  setTab(newTab);
 };

 const handleLikeClick = async (event) => {
  event.preventDefault();
  event.stopPropagation();

  if (!user) {
   const result = await confirm({
    title: "Sign in required",
    description: `Sign in to add to favourites?`,
    confirmLabel: "Sign in",
    cancelLabel: "Cancel",
   });

   if (result) {
    window.location.href = API_ENDPOINTS.AUTH.SIGN_IN;
   }

   return;
  }

  if (liked) {
   unlikeDeal(deal.id);
  } else {
   likeDeal(deal.id);
  }
 };

 if (loading) return <DealPageSkeleton />;
 if (error)
  return (
   <BasePage>
    <NotificationTypography
     color="error"
     Icon={DangerousIcon}
     title="Something went wrong"
     message="Please try again"
     showActions={true}
     backTo="/deals"
    />
   </BasePage>
  );
 if (!deal)
  return (
   <BasePage>
    <NotificationTypography
     color="error"
     Icon={SearchOffIcon}
     title="Deal Not Found"
     message="This deal may have expired or been removed."
     showActions={true}
     backTo="/search"
    />
   </BasePage>
  );

 const liked = user && deal.likedBy?.includes(user.uid);

 const orderedDays = (deal.daysOfTheWeek || [])
  .slice()
  .sort((a, b) => originalDays.indexOf(a) - originalDays.indexOf(b));

 const today = new Date().toLocaleDateString("en-US", { weekday: "long" });
 const isOwner = user?.uid === deal.userId;

 // Statuses that should HIDE the deal
 const hiddenStatuses = ["disabled", "pendingDelete", "deleted"];

 // Check if deal or restaurant has a hidden status
 const isDealHidden = deal.status && hiddenStatuses.includes(deal.status);
 const isRestaurantHidden =
  deal.restaurantStatus && hiddenStatuses.includes(deal.restaurantStatus);

 // SHOW the deal if:
 // - Either deal OR restaurant has a visible status
 // - AND neither has a hidden status
 const shouldShowDeal =
  !isDealHidden &&
  !isRestaurantHidden &&
  (deal.status === "active" ||
   (isOwner && ["pendingCreate", "pendingEdit"].includes(deal.status)));

 if (shouldShowDeal) {
  return (
   <BasePage
    showBreadcrumbs={true}
    breadcrumbProps={{
     restaurantId: deal.restaurantId,
     restaurantDisplayName: deal.restaurantName,
     dealName: deal.title,
    }}
   >
    <Grid container spacing={4}>
     {user?.uid === deal.userId &&
      deal.status &&
      ["pendingCreate", "pendingEdit", "pendingDelete"].includes(
       deal.status,
      ) && (
       <Alert severity="warning" sx={{ borderRadius: "12px", width: "100%" }}>
        {getStatusWarningMessage(deal.status, "food")}
       </Alert>
      )}
     <Grid size={{ xs: 12, md: 6 }}>
      <Carousel
       dots
       slidesToShow={1}
       padding={0}
       width="400%"
       height="400px"
       borderRadius="12px"
       images={deal.imageUrls}
      />
     </Grid>
     <Grid size={{ xs: 12, md: 6 }}>
      <Card sx={{ height: { xs: "auto", md: "400px", borderRadius: "12px" } }}>
       <CardHeader
        sx={{ paddingBottom: 0 }}
        action={
         <Box>
          <LikeChip
           count={deal.likedBy?.length || 0}
           liked={liked}
           onClick={handleLikeClick}
          />
          <ShareButton restaurantId={deal.restaurantId} dealId={deal.id} />
          <ChatButton
           otherUser={{
            id: deal.userId,
            type: 'vendor',
            name: deal.restaurantName || 'Vendor',
           }}
           context="deal"
           contextData={{
            dealId: deal.id,
            dealTitle: deal.title,
            restaurantId: deal.restaurantId,
            restaurantName: deal.restaurantName,
           }}
           disabled={isOwner}
          />
         </Box>
        }
        title={deal.title}
        subheader={
         <Link
          underline="hover"
          component={RouterLink}
          color="text.secondary"
          to={`/restaurant/${deal.restaurantId}`}
         >
          {deal.restaurantName}
         </Link>
        }
       />
       <CardContent
        sx={{
         paddingTop: 0,
         display: "flex",
         flexDirection: "column",
         gap: 1,
        }}
       >
        <RatingTypography
         reviews={deal.reviewCount}
         rating={deal.rating}
         editable={false}
         page
        />
        <PriceTypography
         price={deal.price}
         originalPrice={deal.originalPrice}
        />
        <Typography variant="body2" color="text.secondary" mt={1}>
         <EventAvailableIcon
          fontSize="small"
          sx={{ mr: 0.5, verticalAlign: "middle" }}
         />
         {orderedDays.map((day, index) => (
          <Box
           key={day}
           component="span"
           sx={{
            fontWeight: day === today ? "bold" : "normal",
            display: "inline",
           }}
          >
           {day}
           {index < orderedDays.length - 1 ? ", " : ""}
          </Box>
         ))}
        </Typography>
        <TimeTypography
         start={deal.timeStart}
         end={deal.timeEnd}
         allDay={deal.isAllDay}
         daysOfTheWeek={deal.daysOfTheWeek}
        />
        <Typography
         variant="body2"
         color="text.secondary"
         sx={{ display: "flex", alignItems: "center" }}
        >
         {deal.isDineIn && (
          <>
           <TableRestaurantIcon
            fontSize="small"
            sx={{ mr: 0.5, verticalAlign: "middle" }}
           />
           Dine in
          </>
         )}
         {deal.isDineIn && deal.isTakeaway !== undefined && (
          <Box component="span" sx={{ mx: 1, opacity: 0.5 }}>
           |
          </Box>
         )}
         {deal.isTakeaway !== undefined && (
          <span>
           {deal.isTakeaway ? (
            <>
             <FastfoodIcon
              fontSize="small"
              sx={{ mr: 0.5, verticalAlign: "middle" }}
             />
             Takeaway
            </>
           ) : (
            <>
             <NoFoodIcon
              fontSize="small"
              sx={{ mr: 0.5, verticalAlign: "middle", opacity: 0.5 }}
             />
             No Takeaway
            </>
           )}
          </span>
         )}
        </Typography>

        {deal.dietaryPreferences?.length > 0 && (
         <Box display="flex" flexWrap="wrap">
          {deal.dietaryPreferences.map((pref, i) => (
           <Box key={i} mr={1} mb={1}>
            <Chip component="span" size="small" label={pref} />
           </Box>
          ))}
         </Box>
        )}
        {Array.isArray(deal.locations) && deal.locations.length > 0 && (
         <ChipButton locations={deal.locations} />
        )}
       </CardContent>
      </Card>
     </Grid>
     <Grid size={12}>
      <TabContext value={tab}>
       <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <TabList onChange={handleTabChange}>
         <Tab label="Related deals" value="deals" />
         <Tab label="Reviews" value="reviews" />
        </TabList>
       </Box>
       <TabPanel value="deals" sx={{ px: 0 }}>
        <RelatedDealGrid currentDeal={deal} />
       </TabPanel>
       <TabPanel value="reviews" sx={{ px: 0 }}>
        <ReviewList deal={deal} isOwner={isOwner} userId={user?.uid} />
       </TabPanel>
      </TabContext>
     </Grid>
    </Grid>
   </BasePage>
  );
 }

 return (
  <BasePage>
   <NotificationTypography
    color="error"
    Icon={SearchOffIcon}
    title="Deal Not Found"
    message="This deal may have expired or been removed."
    showActions={true}
    backTo="/search"
   />
  </BasePage>
 );
}
